/**
 * Telegram WebApp integration helpers for ENERGYM Mini App.
 */

/**
 * Get the Telegram WebApp instance.
 * Returns null if not running inside Telegram.
 */
export function getTelegramWebApp() {
  if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
    return window.Telegram.WebApp;
  }
  return null;
}

/**
 * Check if app is running inside Telegram.
 */
export function isTelegramEnv() {
  return !!getTelegramWebApp();
}

/**
 * Extract user data from Telegram WebApp.initDataUnsafe.
 * Returns normalized user object or null.
 */
export function getUserData() {
  const webApp = getTelegramWebApp();
  if (!webApp?.initDataUnsafe?.user) return null;

  const user = webApp.initDataUnsafe.user;
  return {
    telegramId: user.id,
    firstName: user.first_name || '',
    lastName: user.last_name || '',
    username: user.username || '',
    languageCode: user.language_code || 'en',
    isPremium: user.is_premium || false,
    photoUrl: user.photo_url || null,
  };
}

/**
 * Get raw initData string for server-side validation.
 */
export function getInitData() {
  const webApp = getTelegramWebApp();
  return webApp?.initData || '';
}

/**
 * Get start_param from deep link (e.g., t.me/bot?startapp=referral_123).
 */
export function getStartParam() {
  const webApp = getTelegramWebApp();
  return webApp?.initDataUnsafe?.start_param || null;
}

// -- Haptic Feedback --

/**
 * Haptic impact — for button taps, list selections, drag-and-drop.
 *
 * Priority chain:
 *   1. OmnisioNative bridge (iOS/Android native wrapper via WKScriptMessage)
 *   2. Telegram WebApp HapticFeedback API
 *   3. Web Vibration API fallback
 *   4. Silent no-op (never throws)
 *
 * @param {'light' | 'medium' | 'heavy' | 'rigid' | 'soft'} style
 */
/** User can disable haptics in Settings (persisted in localStorage). */
function hapticsDisabled() {
  try { return typeof localStorage !== 'undefined' && localStorage.getItem('omnisio_workout_haptics') === 'false'; }
  catch { return false; }
}

export function hapticImpact(style = 'light') {
  if (hapticsDisabled()) return;
  try {
    // 1. Native bridge (Omnisio iOS/Android shell)
    if (typeof window !== 'undefined' && window.OmnisioNative?.hapticImpact) {
      window.OmnisioNative.hapticImpact(style);
      return;
    }

    // 2. Telegram WebApp
    const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
    if (tg?.HapticFeedback) {
      // Telegram only accepts 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'
      tg.HapticFeedback.impactOccurred(style);
      return;
    }

    // 3. Web Vibration API
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      const ms = { light: 10, medium: 20, heavy: 40, rigid: 30, soft: 8 }[style] ?? 15;
      navigator.vibrate(ms);
    }
    // 4. Silent no-op — all paths handled
  } catch {
    // Never propagate — haptics are enhancement only
  }
}

/**
 * Haptic selection — for picker scrolls, toggle switches, segmented control.
 * Light, subtle feedback.
 */
export function hapticSelection() {
  if (hapticsDisabled()) return;
  try {
    if (typeof window !== 'undefined' && window.OmnisioNative?.hapticSelection) {
      window.OmnisioNative.hapticSelection();
      return;
    }

    const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.selectionChanged();
      return;
    }

    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(8);
    }
  } catch {
    // Silent no-op
  }
}

/**
 * Haptic notification — for success confirmations, warnings, errors.
 * Maps to distinct native patterns so users can feel the outcome type.
 *
 * @param {'success' | 'warning' | 'error'} type
 */
export function hapticNotification(type = 'success') {
  if (hapticsDisabled()) return;
  try {
    if (typeof window !== 'undefined' && window.OmnisioNative?.hapticNotification) {
      window.OmnisioNative.hapticNotification(type);
      return;
    }

    const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.notificationOccurred(type);
      return;
    }

    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      const patterns = {
        success: [10, 60, 12],
        warning: [20, 40, 20, 40, 20],
        error:   [50, 30, 50, 30, 50],
      };
      navigator.vibrate(patterns[type] ?? [10]);
    }
  } catch {
    // Silent no-op
  }
}

// -- Cloud Storage --

export function cloudStorageSet(key, value) {
  return new Promise((resolve, reject) => {
    const webApp = getTelegramWebApp();
    if (!webApp?.CloudStorage) {
      reject(new Error('CloudStorage not available'));
      return;
    }
    webApp.CloudStorage.setItem(key, JSON.stringify(value), (error, success) => {
      if (error) reject(new Error(error));
      else resolve(success);
    });
  });
}

export function cloudStorageGet(key) {
  return new Promise((resolve, reject) => {
    const webApp = getTelegramWebApp();
    if (!webApp?.CloudStorage) {
      reject(new Error('CloudStorage not available'));
      return;
    }
    webApp.CloudStorage.getItem(key, (error, value) => {
      if (error) reject(new Error(error));
      else {
        try {
          resolve(value ? JSON.parse(value) : null);
        } catch {
          resolve(value);
        }
      }
    });
  });
}

export function cloudStorageRemove(key) {
  return new Promise((resolve, reject) => {
    const webApp = getTelegramWebApp();
    if (!webApp?.CloudStorage) {
      reject(new Error('CloudStorage not available'));
      return;
    }
    webApp.CloudStorage.removeItem(key, (error, success) => {
      if (error) reject(new Error(error));
      else resolve(success);
    });
  });
}

// -- Theme --

export function getThemeParams() {
  const webApp = getTelegramWebApp();
  return webApp?.themeParams || {};
}

export function getColorScheme() {
  const webApp = getTelegramWebApp();
  return webApp?.colorScheme || 'dark';
}

export function getPlatform() {
  const webApp = getTelegramWebApp();
  return webApp?.platform || 'unknown';
}

// -- UI Controls --

export function expandApp() {
  const webApp = getTelegramWebApp();
  webApp?.expand();
}

export function readyApp() {
  const webApp = getTelegramWebApp();
  webApp?.ready();
}

export function closeApp() {
  const webApp = getTelegramWebApp();
  webApp?.close();
}

export function setHeaderColor(color) {
  const webApp = getTelegramWebApp();
  try {
    webApp?.setHeaderColor(color);
  } catch {
    // Older clients may not support this
  }
}

export function setBackgroundColor(color) {
  const webApp = getTelegramWebApp();
  try {
    webApp?.setBackgroundColor(color);
  } catch {
    // Older clients may not support this
  }
}
